/**
 * Create Voice Room Modal
 */

import { useState } from 'react';
import { useCreateVoiceRoom } from '@/hooks/useVoiceRooms';
import { useDialogA11y } from '@/hooks/useDialogA11y';

interface CreateVoiceRoomModalProps {
  onClose: () => void;
}

export const CreateVoiceRoomModal = ({ onClose }: CreateVoiceRoomModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { dialogRef, titleId } = useDialogA11y(onClose);

  const createMutation = useCreateVoiceRoom();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Името на стаята е задължително');
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Грешка при създаване на стая');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50" onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className="bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Създай гласова стая
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="room-name" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Име на стаята *
            </label>
            <input
              id="room-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder="например: Общ офис, Среща"
              maxLength={100}
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="room-description" className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Описание
            </label>
            <textarea
              id="room-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input resize-none"
              rows={2}
              placeholder="Кратко описание на стаята..."
              maxLength={500}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={createMutation.isPending}>
              Отказ
            </button>
            <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Създаване...' : 'Създай стая'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
